import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, User, Receipt, Calculator } from 'lucide-react';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { KpiData } from '@/api/statistics';

interface KpiCardsProps {
  data: KpiData;
  currency: string;
}

interface KpiCardDef {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  getValue: (d: KpiData) => number;
  isCount?: boolean;
}

function formatDisplay(amount: number, currency: string): string {
  const minorUnits = getMinorUnitsSync(currency);
  return formatMinorAmount(amount, currency, minorUnits);
}

const cards: KpiCardDef[] = [
  {
    key: 'totalSpent',
    labelKey: 'web:kpiCards.totalSpent',
    icon: TrendingUp,
    iconBg: 'bg-brand-100 dark:bg-brand-900/40',
    iconColor: 'text-brand-600',
    getValue: (d) => d.totalSpent,
  },
  {
    key: 'yourShare',
    labelKey: 'web:kpiCards.yourShare',
    icon: User,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600',
    getValue: (d) => d.yourShare,
  },
  {
    key: 'expenseCount',
    labelKey: 'web:kpiCards.expenses',
    icon: Receipt,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600',
    getValue: (d) => d.expenseCount,
    isCount: true,
  },
  {
    key: 'avgPerExpense',
    labelKey: 'web:kpiCards.avgExpense',
    icon: Calculator,
    iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    iconColor: 'text-violet-600',
    getValue: (d) => d.avgPerExpense,
  },
];

export function KpiCards({ data, currency }: KpiCardsProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.map(card => (
        <div
          key={card.key}
          className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary"
        >
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${card.iconBg}`}>
              <card.icon className={`h-5 w-5 ${card.iconColor}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-text-tertiary">{t(card.labelKey)}</p>
              <p className="truncate text-lg font-semibold text-text-primary">
                {card.isCount
                  ? card.getValue(data)
                  : formatDisplay(card.getValue(data), currency)}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
